import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Table,
  Tabs,
  Space,
  Row,
  Col,
  Divider,
  Modal,
  message,
  Tooltip,
  Tag,
  Steps,
  Typography,
  Collapse,
  Switch
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  CalculatorOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import operationService, { Operation } from '../../services/operation.service';
import { useCreateEstimation } from '../../hooks/useEstimation';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

interface MaterialItem {
  key: string;
  itemId: string;
  description: string;
  quantity: number;
  uom: string;
  unitCost: number;
  totalCost: number;
  costBasis: string;
  supplierId?: string;
  leadTimeDays: number;
  confidenceLevel: string;
}

interface OperationItem {
  key: string;
  operationId: string;
  description: string;
  workstationId: string;
  setupTimeHours: number;
  runTimePerUnitHours: number;
  quantity: number;
  laborRate: number;
  overheadRate: number;
  machineRate: number;
  totalCost: number;
  isOutsourced: boolean;
  outsourceCost?: number;
}

const EstimationBuilderPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const linkedOpportunityId =
    searchParams.get('crm_opportunity_id') || searchParams.get('opportunity_id') || undefined;
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const createEstimationMutation = useCreateEstimation();

  // Material items state
  const [materials, setMaterials] = useState<MaterialItem[]>([]);
  const [materialModalVisible, setMaterialModalVisible] = useState(false);
  const [materialForm] = Form.useForm();

  // Operation items state
  const [operations, setOperations] = useState<OperationItem[]>([]);
  const [operationModalVisible, setOperationModalVisible] = useState(false);
  const [operationForm] = Form.useForm();
  const [operationsMaster, setOperationsMaster] = useState<Operation[]>([]);
  const [isLoadingOperationsMaster, setIsLoadingOperationsMaster] = useState(false);

  // Cost summary state
  const [costSummary, setCostSummary] = useState({
    totalMaterialCost: 0,
    totalLaborCost: 0,
    totalOverheadCost: 0,
    totalToolingCost: 0,
    totalCost: 0,
    marginPercentage: 25,
    sellingPrice: 0
  });

  // Material columns
  const materialColumns = [
    {
      title: 'Item',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      width: 80,
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unitCost',
      key: 'unitCost',
      width: 120,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: 'Total Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 120,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: 'Cost Basis',
      dataIndex: 'costBasis',
      key: 'costBasis',
      width: 120,
      render: (value: string) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: 'Confidence',
      dataIndex: 'confidenceLevel',
      key: 'confidenceLevel',
      width: 100,
      render: (value: string) => {
        const color = value === 'High' ? 'green' : value === 'Medium' ? 'orange' : 'red';
        return <Tag color={color}>{value}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: MaterialItem) => (
        <Space>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteMaterial(record.key)}
          />
        </Space>
      ),
    },
  ];

  // Operation columns
  const operationColumns = [
    {
      title: 'Operation',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Setup (hrs)',
      dataIndex: 'setupTimeHours',
      key: 'setupTimeHours',
      width: 100,
    },
    {
      title: 'Run Time (hrs/unit)',
      dataIndex: 'runTimePerUnitHours',
      key: 'runTimePerUnitHours',
      width: 140,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
    },
    {
      title: 'Labor Rate',
      dataIndex: 'laborRate',
      key: 'laborRate',
      width: 100,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: 'Total Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 120,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: 'Outsourced',
      dataIndex: 'isOutsourced',
      key: 'isOutsourced',
      width: 100,
      render: (value: boolean) => value ? <Tag color="purple">Yes</Tag> : <Tag>No</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: OperationItem) => (
        <Space>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteOperation(record.key)}
          />
        </Space>
      ),
    },
  ];

  // Fetch operations master data
  React.useEffect(() => {
    const fetchOperationsMaster = async () => {
      try {
        setIsLoadingOperationsMaster(true);
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user.company_id) {
            const ops = await operationService.getAll(user.company_id, true);
            setOperationsMaster(ops);
          }
        }
      } catch (error) {
        console.error('Failed to fetch operations:', error);
        message.error('Failed to load operations master data');
      } finally {
        setIsLoadingOperationsMaster(false);
      }
    };

    fetchOperationsMaster();
  }, []);

  const handleAddMaterial = () => {
    materialForm.resetFields();
    setMaterialModalVisible(true);
  };

  const handleMaterialModalOk = async () => {
    try {
      const values = await materialForm.validateFields();
      const newMaterial: MaterialItem = {
        key: Date.now().toString(),
        itemId: values.itemId || `ITEM-${Date.now()}`,
        description: values.description,
        quantity: values.quantity,
        uom: values.uom,
        unitCost: values.unitCost,
        totalCost: values.quantity * values.unitCost,
        costBasis: values.costBasis,
        supplierId: values.supplierId,
        leadTimeDays: values.leadTimeDays || 0,
        confidenceLevel: values.confidenceLevel
      };
      setMaterials([...materials, newMaterial]);
      setMaterialModalVisible(false);
      message.success('Material added successfully');
      recalculateCosts([...materials, newMaterial], operations);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleDeleteMaterial = (key: string) => {
    const newMaterials = materials.filter(m => m.key !== key);
    setMaterials(newMaterials);
    recalculateCosts(newMaterials, operations);
    message.success('Material removed');
  };

  const handleAddOperation = () => {
    operationForm.resetFields();
    setOperationModalVisible(true);
  };

  const handleOperationModalOk = async () => {
    try {
      const values = await operationForm.validateFields();
      const totalHours = values.setupTimeHours + (values.runTimePerUnitHours * values.quantity);
      const totalCost = values.isOutsourced
        ? values.outsourceCost || 0
        : totalHours * (values.laborRate + values.overheadRate + values.machineRate);

      const newOperation: OperationItem = {
        key: Date.now().toString(),
        operationId: values.operationId || `OP-${Date.now()}`,
        description: values.description,
        workstationId: values.workstationId,
        setupTimeHours: values.setupTimeHours,
        runTimePerUnitHours: values.runTimePerUnitHours,
        quantity: values.quantity,
        laborRate: values.laborRate || 0,
        overheadRate: values.overheadRate || 0,
        machineRate: values.machineRate || 0,
        totalCost: totalCost,
        isOutsourced: values.isOutsourced || false,
        outsourceCost: values.outsourceCost
      };
      setOperations([...operations, newOperation]);
      setOperationModalVisible(false);
      message.success('Operation added successfully');
      recalculateCosts(materials, [...operations, newOperation]);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleDeleteOperation = (key: string) => {
    const newOperations = operations.filter(o => o.key !== key);
    setOperations(newOperations);
    recalculateCosts(materials, newOperations);
    message.success('Operation removed');
  };

  const recalculateCosts = (mats: MaterialItem[], ops: OperationItem[]) => {
    const totalMaterialCost = mats.reduce((sum, m) => sum + m.totalCost, 0);
    const totalLaborCost = ops.reduce((sum, o) => {
      if (o.isOutsourced) return sum;
      const totalHours = o.setupTimeHours + (o.runTimePerUnitHours * o.quantity);
      return sum + (totalHours * o.laborRate);
    }, 0);
    const totalOverheadCost = ops.reduce((sum, o) => {
      if (o.isOutsourced) return sum;
      const totalHours = o.setupTimeHours + (o.runTimePerUnitHours * o.quantity);
      return sum + (totalHours * o.overheadRate);
    }, 0);
    const totalToolingCost = ops.reduce((sum, o) => {
      if (o.isOutsourced) return sum + (o.outsourceCost || 0);
      const totalHours = o.setupTimeHours + (o.runTimePerUnitHours * o.quantity);
      return sum + (totalHours * o.machineRate);
    }, 0);

    const totalCost = totalMaterialCost + totalLaborCost + totalOverheadCost + totalToolingCost;
    const marginPercentage = costSummary.marginPercentage;
    const sellingPrice = totalCost * (1 + marginPercentage / 100);

    setCostSummary({
      totalMaterialCost,
      totalLaborCost,
      totalOverheadCost,
      totalToolingCost,
      totalCost,
      marginPercentage,
      sellingPrice
    });
  };

  const handleMarginChange = (value: number | null) => {
    if (value !== null) {
      const newMargin = value;
      const sellingPrice = costSummary.totalCost * (1 + newMargin / 100);
      setCostSummary({
        ...costSummary,
        marginPercentage: newMargin,
        sellingPrice
      });
    }
  };

  const handleCalculateEstimation = async () => {
    setCalculating(true);
    try {
      // Simulate API call to calculate estimation with backend
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success('Estimation calculated successfully!');
    } catch (error) {
      message.error('Failed to calculate estimation');
    } finally {
      setCalculating(false);
    }
  };

  const handleSaveEstimation = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const estimationData = {
        estimation_name: values.estimationName,
        customer_name: values.customerId,
        project_name: values.productId,
        currency: 'AED',
        description: values.notes,
        opportunity_id: linkedOpportunityId,
        department_id: values.department_id,
      };

      console.log('Saving estimation:', estimationData);

      await createEstimationMutation.mutateAsync(estimationData as any);

      message.success('Estimation saved successfully!');
      setTimeout(() => {
        navigate('/growth/estimations');
      }, 1000);
    } catch (error) {
      console.error('Save failed:', error);
      message.error('Failed to save estimation');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: 'Basic Info',
      icon: <InfoCircleOutlined />
    },
    {
      title: 'Materials',
      icon: <PlusOutlined />
    },
    {
      title: 'Operations',
      icon: <CalculatorOutlined />
    },
    {
      title: 'Review & Save',
      icon: <CheckCircleOutlined />
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2}>
          {id ? 'Edit Cost Estimation' : 'Create New Cost Estimation'}
        </Title>
        <Text type="secondary">
          Build a detailed cost estimation with materials, operations, and pricing
        </Text>

        <Divider />

        <Steps current={currentStep} items={steps} style={{ marginBottom: 32 }} />

        <Form form={form} layout="vertical">
          {/* Step 0: Basic Information */}
          {currentStep === 0 && (
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="estimationName"
                  label="Estimation Name"
                  rules={[{ required: true, message: 'Please enter estimation name' }]}
                >
                  <Input placeholder="e.g., Custom Widget Assembly" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="estimationNumber"
                  label="Estimation Number"
                  rules={[{ required: true, message: 'Please enter estimation number' }]}
                >
                  <Input placeholder="e.g., EST-2025-001" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="customerId" label="Customer">
                  <Select placeholder="Select customer" allowClear>
                    <Option value="cust-001">ACME Corporation</Option>
                    <Option value="cust-002">TechCorp Industries</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="productId" label="Product/Item">
                  <Select placeholder="Select product" allowClear>
                    <Option value="prod-001">Widget A</Option>
                    <Option value="prod-002">Widget B</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="department_id"
                  label="Department"
                  rules={[{ required: true, message: 'Department is required' }]}
                >
                  <DepartmentSelect />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="quantity"
                  label="Quantity"
                  rules={[{ required: true, message: 'Please enter quantity' }]}
                  initialValue={1}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="estimationDate"
                  label="Estimation Date"
                  rules={[{ required: true, message: 'Please select date' }]}
                  initialValue={dayjs()}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="validUntilDate" label="Valid Until">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="notes" label="Notes">
                  <TextArea rows={3} placeholder="Additional notes or requirements" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Step 1: Materials */}
          {currentStep === 1 && (
            <div>
              <Space style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddMaterial}
                >
                  Add Material
                </Button>
                <Text type="secondary">
                  Total Materials: {materials.length} | Total Cost: ${costSummary.totalMaterialCost.toFixed(2)}
                </Text>
              </Space>
              <Table
                columns={materialColumns}
                dataSource={materials}
                pagination={false}
                scroll={{ x: 1000 }}
              />
            </div>
          )}

          {/* Step 2: Operations */}
          {currentStep === 2 && (
            <div>
              <Space style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddOperation}
                >
                  Add Operation
                </Button>
                <Text type="secondary">
                  Total Operations: {operations.length} | Total Cost: ${(costSummary.totalLaborCost + costSummary.totalOverheadCost + costSummary.totalToolingCost).toFixed(2)}
                </Text>
              </Space>
              <Table
                columns={operationColumns}
                dataSource={operations}
                pagination={false}
                scroll={{ x: 1200 }}
              />
            </div>
          )}

          {/* Step 3: Review & Save */}
          {currentStep === 3 && (
            <Row gutter={24}>
              <Col span={24}>
                <Card title="Cost Summary" style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text strong>Total Material Cost:</Text>
                      <div style={{ fontSize: 24, color: '#145A54' }}>
                        ${costSummary.totalMaterialCost.toFixed(2)}
                      </div>
                    </Col>
                    <Col span={8}>
                      <Text strong>Total Labor Cost:</Text>
                      <div style={{ fontSize: 24, color: '#52c41a' }}>
                        ${costSummary.totalLaborCost.toFixed(2)}
                      </div>
                    </Col>
                    <Col span={8}>
                      <Text strong>Total Overhead Cost:</Text>
                      <div style={{ fontSize: 24, color: '#faad14' }}>
                        ${costSummary.totalOverheadCost.toFixed(2)}
                      </div>
                    </Col>
                  </Row>
                  <Divider />
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text strong>Total Cost:</Text>
                      <div style={{ fontSize: 28, color: '#000' }}>
                        ${costSummary.totalCost.toFixed(2)}
                      </div>
                    </Col>
                    <Col span={8}>
                      <Text strong>Margin %:</Text>
                      <InputNumber
                        min={0}
                        max={100}
                        value={costSummary.marginPercentage}
                        onChange={handleMarginChange}
                        style={{ width: '100%', fontSize: 24 }}
                        suffix="%"
                      />
                    </Col>
                    <Col span={8}>
                      <Text strong>Selling Price:</Text>
                      <div style={{ fontSize: 28, color: '#52c41a' }}>
                        ${costSummary.sellingPrice.toFixed(2)}
                      </div>
                    </Col>
                  </Row>
                </Card>

                <Collapse defaultActiveKey={['materials', 'operations']}>
                  <Panel header={`Materials (${materials.length})`} key="materials">
                    <Table
                      columns={materialColumns.filter(c => c.key !== 'actions')}
                      dataSource={materials}
                      pagination={false}
                      size="small"
                    />
                  </Panel>
                  <Panel header={`Operations (${operations.length})`} key="operations">
                    <Table
                      columns={operationColumns.filter(c => c.key !== 'actions')}
                      dataSource={operations}
                      pagination={false}
                      size="small"
                    />
                  </Panel>
                </Collapse>
              </Col>
            </Row>
          )}
        </Form>

        <Divider />

        <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={() => navigate('/growth/estimations')}>Cancel</Button>
          <Space>
            {currentStep > 0 && (
              <Button onClick={() => setCurrentStep(currentStep - 1)}>Previous</Button>
            )}
            {currentStep < 3 && (
              <Button type="primary" onClick={() => setCurrentStep(currentStep + 1)}>
                Next
              </Button>
            )}
            {currentStep === 3 && (
              <>
                <Button
                  icon={<CalculatorOutlined />}
                  onClick={handleCalculateEstimation}
                  loading={calculating}
                >
                  Recalculate
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveEstimation}
                  loading={loading}
                >
                  Save Estimation
                </Button>
              </>
            )}
          </Space>
        </Space>
      </Card>

      {/* Material Modal */}
      <Modal
        title="Add Material"
        open={materialModalVisible}
        onOk={handleMaterialModalOk}
        onCancel={() => setMaterialModalVisible(false)}
        width={600}
      >
        <Form form={materialForm} layout="vertical">
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input placeholder="e.g., Steel Sheet 2mm" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="uom"
                label="UOM"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select placeholder="Select UOM">
                  <Option value="pcs">Pieces</Option>
                  <Option value="kg">Kilograms</Option>
                  <Option value="m">Meters</Option>
                  <Option value="sqm">Square Meters</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="unitCost"
                label="Unit Cost ($)"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="costBasis"
                label="Cost Basis"
                rules={[{ required: true, message: 'Required' }]}
                initialValue="Estimated"
              >
                <Select>
                  <Option value="Standard Cost">Standard Cost</Option>
                  <Option value="Average Cost">Average Cost</Option>
                  <Option value="Last Purchase">Last Purchase</Option>
                  <Option value="Supplier Quote">Supplier Quote</Option>
                  <Option value="Estimated">Estimated</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="leadTimeDays" label="Lead Time (Days)" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confidenceLevel"
                label="Confidence Level"
                initialValue="Medium"
              >
                <Select>
                  <Option value="High">High</Option>
                  <Option value="Medium">Medium</Option>
                  <Option value="Low">Low</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Operation Modal */}
      <Modal
        title="Add Operation"
        open={operationModalVisible}
        onOk={handleOperationModalOk}
        onCancel={() => setOperationModalVisible(false)}
        width={600}
      >
        <Form form={operationForm} layout="vertical">
          <Form.Item
            name="operationId"
            label="Operation"
            rules={[{ required: true, message: 'Please select operation' }]}
          >
            <Select
              placeholder="Select operation"
              loading={isLoadingOperationsMaster}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => {
                const selectedOp = operationsMaster.find(op => op.id === value);
                if (selectedOp) {
                  operationForm.setFieldsValue({
                    description: `${selectedOp.operation_name} - ${selectedOp.description || ''}`,
                    setupTimeHours: selectedOp.standard_setup_time_hours,
                    runTimePerUnitHours: selectedOp.standard_run_time_per_unit_hours,
                    laborRate: Number(selectedOp.per_hour_cost ?? 0),
                  });
                }
              }}
              options={operationsMaster.map(op => ({
                value: op.id,
                label: `${op.operation_code} - ${op.operation_name}`
              }))}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Operation Description"
            hidden
          >
            <Input />
          </Form.Item>
          <Form.Item name="workstationId" label="Workstation">
            <Select placeholder="Select workstation">
              <Option value="ws-001">CNC Machine 1</Option>
              <Option value="ws-002">Assembly Line A</Option>
              <Option value="ws-003">Welding Station</Option>
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="setupTimeHours"
                label="Setup Time (hrs)"
                rules={[{ required: true, message: 'Required' }]}
                initialValue={0}
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="runTimePerUnitHours"
                label="Run Time/Unit (hrs)"
                rules={[{ required: true, message: 'Required' }]}
                initialValue={0}
              >
                <InputNumber min={0} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Required' }]}
                initialValue={1}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="isOutsourced" label="Outsourced" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.isOutsourced !== currentValues.isOutsourced
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('isOutsourced') ? (
                <Form.Item
                  name="outsourceCost"
                  label="Outsource Cost ($)"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              ) : (
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="laborRate"
                      label="Labor Rate ($/hr)"
                      initialValue={25}
                    >
                      <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="overheadRate"
                      label="Overhead Rate ($/hr)"
                      initialValue={15}
                    >
                      <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="machineRate"
                      label="Machine Rate ($/hr)"
                      initialValue={10}
                    >
                      <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              )
            }
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EstimationBuilderPage;

